import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type ServiceBinding,
} from "@nasam/kernel";
import {
  branchesRepositoryToken,
  companiesRepositoryToken,
  departmentsRepositoryToken,
} from "../../contracts";
import type {
  AccountAdminsRepository,
  BranchManagementRepository,
  CountriesRepository,
  CurrenciesRepository,
  RegionsRepository,
} from "./domain";
import {
  createAccountAdminsHttpRepository,
  createBranchesHttpRepository,
  createBranchManagementRepository,
  createCompaniesHttpRepository,
  createCountriesHttpRepository,
  createCurrenciesHttpRepository,
  createDepartmentsRepository,
  createRegionsHttpRepository,
} from "./data";
import { createOrganizationRoutes } from "./presentation/routes";
import {
  ADMINS_VIEW,
  BRANCHES_VIEW,
  COMPANIES_VIEW,
  DEPARTMENTS_VIEW,
  INDIVIDUALS_VIEW,
  REGIONS_VIEW,
} from "./organizationPermissions";

export {
  branchesRepositoryToken,
  companiesRepositoryToken,
  departmentsRepositoryToken,
};
export * from "./organizationPermissions";
export const accountAdminsRepositoryToken =
  createServiceToken<AccountAdminsRepository>(
    "organization.account-admins-repository",
  );
export const currenciesRepositoryToken =
  createServiceToken<CurrenciesRepository>(
    "organization.currencies-repository",
  );
export const regionsRepositoryToken = createServiceToken<RegionsRepository>(
  "organization.regions-repository",
);
export const branchManagementRepositoryToken =
  createServiceToken<BranchManagementRepository>(
    "organization.branch-management-repository",
  );
export const countriesRepositoryToken = createServiceToken<CountriesRepository>(
  "organization.countries-repository",
);

function createOrganizationServiceBindings(
  httpClient: Parameters<
    NonNullable<AppFeatureDefinition["provideServices"]>
  >[0]["httpClient"],
): readonly ServiceBinding[] {
  const regions: RegionsRepository = createRegionsHttpRepository(httpClient);
  // Full Branch Management CRUD is real (see createBranchManagementRepository).
  // Deliberately a SEPARATE repository/token from branchesRepositoryToken
  // below, whose `list` already backs the Create Asset form's Branch picker
  // through its own narrower contract -- kept independent so a change to one
  // surface can never regress the other.
  const branchManagement: BranchManagementRepository =
    createBranchManagementRepository(httpClient);

  return [
    bindService(
      companiesRepositoryToken,
      createCompaniesHttpRepository(httpClient),
    ),
    bindService(
      accountAdminsRepositoryToken,
      createAccountAdminsHttpRepository(httpClient),
    ),
    bindService(
      currenciesRepositoryToken,
      createCurrenciesHttpRepository(httpClient),
    ),
    bindService(
      branchesRepositoryToken,
      createBranchesHttpRepository(httpClient),
    ),
    bindService(regionsRepositoryToken, regions),
    bindService(branchManagementRepositoryToken, branchManagement),
    bindService(
      departmentsRepositoryToken,
      createDepartmentsRepository(httpClient),
    ),
    bindService(
      countriesRepositoryToken,
      createCountriesHttpRepository(httpClient),
    ),
  ];
}

export const organizationFeature: AppFeatureDefinition = {
  id: "organization",
  version: "0.1.0",
  titleKey: "companies:moduleTitle",
  icon: "Building2",
  basePath: "/organization",
  requiredPermissions: [
    COMPANIES_VIEW,
    ADMINS_VIEW,
    REGIONS_VIEW,
    BRANCHES_VIEW,
    DEPARTMENTS_VIEW,
    INDIVIDUALS_VIEW,
  ],
  // All CORE features share a single "core" rail node (see
  // mergeFeatureNavNodes) — this feature contributes the "organization" drawer
  // group under it.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "organization",
          titleKey: "companies:moduleTitle",
          icon: "Building2",
          placement: "drawer",
          order: 10,
          requiredPermissions: [
            COMPANIES_VIEW,
            ADMINS_VIEW,
            REGIONS_VIEW,
            BRANCHES_VIEW,
            DEPARTMENTS_VIEW,
            INDIVIDUALS_VIEW,
          ],
          children: [
            // A plain leaf, not a nested group: it lands on the companies
            // list, which carries its own in-page tab across to Admins
            // (/organization/admins) — that route stays live, it just isn't
            // a second, redundant drawer entry. It counts as this tab, so the
            // drawer keeps Companies highlighted there and the breadcrumb
            // reads ".../Companies/Account Admins".
            {
              id: "organization-companies",
              titleKey: "companies:nav.companies",
              icon: "Briefcase",
              placement: "drawer",
              order: 10,
              path: "/organization/companies",
              activePaths: ["/organization/admins"],
              requiredPermissions: [COMPANIES_VIEW],
            },
            {
              id: "organization-individuals",
              titleKey: "companies:nav.individuals",
              icon: "User",
              placement: "drawer",
              order: 20,
              path: "/organization/individuals",
              requiredPermissions: [INDIVIDUALS_VIEW],
            },
            {
              id: "organization-regions",
              titleKey: "companies:nav.regions",
              icon: "Map",
              placement: "drawer",
              order: 30,
              path: "/organization/regions",
              requiredPermissions: [REGIONS_VIEW],
            },
            {
              id: "organization-branches",
              titleKey: "companies:nav.branches",
              icon: "Building",
              placement: "drawer",
              order: 40,
              path: "/organization/branches",
              requiredPermissions: [BRANCHES_VIEW],
            },
            {
              id: "organization-departments",
              titleKey: "companies:nav.departments",
              icon: "Folder",
              placement: "drawer",
              order: 50,
              path: "/organization/departments",
              requiredPermissions: [DEPARTMENTS_VIEW],
            },
          ],
        },
      ],
    },
  ],
  routes: (context) => {
    // Repositories resolve once here; each area's factory receives them, so no
    // screen reaches into the service container and mock-vs-HTTP stays a
    // single binding change.
    return createOrganizationRoutes({
      context,
      companies: context.services.resolve(companiesRepositoryToken),
      accountAdmins: context.services.resolve(accountAdminsRepositoryToken),
      currencies: context.services.resolve(currenciesRepositoryToken),
      regions: context.services.resolve(regionsRepositoryToken),
      branchManagement: context.services.resolve(branchManagementRepositoryToken),
      departments: context.services.resolve(departmentsRepositoryToken),
      countries: context.services.resolve(countriesRepositoryToken),
    });
  },
  i18n: {
    namespace: "companies",
    load: {
      en: () => import("../../i18n/locales/en/companies"),
      ar: () => import("../../i18n/locales/ar/companies"),
    },
  },
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "companies:permissions.core",
      children: [
        {
          segment: "organization_management",
          labelKey: "companies:permissions.organizationManagement",
          children: [
            {
              segment: "organization_management_list",
              labelKey: "companies:permissions.companies",
              actions: [
                { id: "view", labelKey: "companies:actions.view" },
                { id: "create", labelKey: "companies:actions.create" },
                { id: "update", labelKey: "companies:actions.update" },
                { id: "delete", labelKey: "companies:actions.delete" },
              ],
            },
            {
              segment: "account_admin_management_list",
              labelKey: "companies:permissions.admins",
              actions: [
                { id: "view", labelKey: "companies:actions.view" },
                { id: "create", labelKey: "companies:actions.create" },
                { id: "update", labelKey: "companies:actions.update" },
                { id: "delete", labelKey: "companies:actions.delete" },
              ],
            },
            {
              segment: "region_management_list",
              labelKey: "companies:permissions.regions",
              actions: [
                { id: "view", labelKey: "companies:actions.view" },
                { id: "create", labelKey: "companies:actions.create" },
                { id: "update", labelKey: "companies:actions.update" },
                { id: "delete", labelKey: "companies:actions.delete" },
              ],
            },
            {
              segment: "branch_management_list",
              labelKey: "companies:permissions.branches",
              actions: [
                { id: "view", labelKey: "companies:actions.view" },
                { id: "create", labelKey: "companies:actions.create" },
                { id: "update", labelKey: "companies:actions.update" },
                { id: "delete", labelKey: "companies:actions.delete" },
              ],
            },
            {
              segment: "department_management_list",
              labelKey: "companies:permissions.departments",
              actions: [
                { id: "view", labelKey: "companies:actions.view" },
                { id: "create", labelKey: "companies:actions.create" },
                { id: "update", labelKey: "companies:actions.update" },
                { id: "delete", labelKey: "companies:actions.delete" },
              ],
            },
            {
              segment: "individual_management_list",
              labelKey: "companies:permissions.individuals",
              actions: [
                { id: "view", labelKey: "companies:actions.view" },
                { id: "create", labelKey: "companies:actions.create" },
                { id: "update", labelKey: "companies:actions.update" },
                { id: "delete", labelKey: "companies:actions.delete" },
              ],
            },
          ],
        },
      ],
    },
  ],
  provideServices: ({ httpClient }) =>
    createOrganizationServiceBindings(httpClient),
};
